import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { Task } from '@quadro/domain';
import { openEvents } from '../lib/rpc';
import { hud } from '../components/Hud';
import * as api from './api';
import { hasPendingDeletes, isPendingDelete } from './pendingDeletes';

export const keys = {
  tasks: ['tasks'] as const,
  settings: ['settings'] as const,
  people: ['people'] as const,
  routines: ['routines'] as const,
  events: ['events'] as const,
  notes: ['notes'] as const,
  liveMap: ['map', 'live'] as const,
  fileMap: ['map', 'file'] as const,
  exposure: ['map', 'exposure'] as const,
  history: (id: string) => ['history', id] as const,
  holidays: (from: string, to: string) => ['holidays', from, to] as const,
};

/** Tarefas (sem as que estão sendo excluídas com "Desfazer" na tela, mesmo que o servidor ainda as mande). */
export const useTasks = () => useQuery({
  queryKey: keys.tasks, queryFn: api.fetchTasks,
  select: (list: Task[]) => (hasPendingDeletes() ? list.filter(t => !isPendingDelete(t.id)) : list),
});
export const useSettings = () => useQuery({ queryKey: keys.settings, queryFn: api.fetchSettings });
export const usePeople = () => useQuery({ queryKey: keys.people, queryFn: api.fetchPeople });
export const useRoutines = () => useQuery({ queryKey: keys.routines, queryFn: api.fetchRoutines });
export const useEvents = () => useQuery({ queryKey: keys.events, queryFn: api.fetchEvents });
export const useTemplates = () => useQuery({ queryKey: ['templates'], queryFn: api.fetchTemplates });
export const useNotices = () => useQuery({ queryKey: ['notifications'], queryFn: api.fetchNotices });

/** Outlook não tem aviso em tempo real (é externo): recarrega a cada 5 min e ao voltar para a janela. */
export const useOutlook = (from: string, to: string) => useQuery({
  queryKey: ['outlook', from, to], queryFn: () => api.fetchOutlook(from, to),
  staleTime: 120_000, refetchInterval: 300_000, refetchOnWindowFocus: true, retry: 1,
});
export const useNotes = () => useQuery({ queryKey: keys.notes, queryFn: api.fetchNotes });
export const useTaskHistory = (id: string) => useQuery({ queryKey: keys.history(id), queryFn: () => api.fetchTaskHistory(id) });
export const useLiveMap = (enabled = true) => useQuery({ queryKey: keys.liveMap, queryFn: api.fetchLiveMap, staleTime: 5 * 60_000, enabled });
export const useFileMap = () => useQuery({ queryKey: keys.fileMap, queryFn: api.fetchImportedMap });
export const useOfficialExposure = (enabled: boolean) => useQuery({ queryKey: keys.exposure, queryFn: api.fetchOfficialExposure, staleTime: 10 * 60_000, enabled });
export const useHolidays = (from: string, to: string) =>
  useQuery({ queryKey: keys.holidays(from, to), queryFn: () => api.fetchHolidays(from, to), staleTime: 24 * 3600_000 });

/**
 * Executa uma escrita, mostra erro amigável e invalida os dados afetados.
 * (O Realtime também invalida, mas isso garante a atualização imediata de quem escreveu.)
 */
export function useAction<A extends unknown[]>(fn: (...args: A) => Promise<unknown>, invalidate: readonly (readonly unknown[])[]) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (args: A) => fn(...args),
    onError: (e: Error) => hud(e.message || 'Não foi possível salvar agora. Tente de novo em instantes.', 'info'),
    onSettled: () => Promise.all(invalidate.map(k => qc.invalidateQueries({ queryKey: k }))),
  });
  return (...args: A) => m.mutateAsync(args).catch(() => undefined);
}

/** Atualização otimista de uma tarefa no cache (arrastar, avançar, concluir). */
export function patchTaskInCache(qc: QueryClient, id: string, patch: Partial<Task>) {
  qc.setQueryData<Task[]>(keys.tasks, list => list?.map(t => (t.id === id ? { ...t, ...patch } : t)));
}

const TABLE_KEYS: Record<string, (readonly unknown[])[]> = {
  tasks: [keys.tasks], subtasks: [keys.tasks], routines: [keys.routines, keys.tasks],
  settings: [keys.settings], projects: [keys.settings], themes: [keys.settings], categories: [keys.settings], subcategories: [keys.settings],
  profiles: [keys.people], events: [keys.events], notes: [keys.notes], org_map_imports: [keys.fileMap],
  routine_automations: [['automations']], automation_runs: [['automations']], processes: [['processes']],
  task_comments: [['comments']], notifications: [['notifications']], task_templates: [['templates']],
};

/** Avisos de mudança da API (SSE, já filtrados pelo dono nas notas); a tela recarrega pela API. */
export function useRealtimeSync(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const pending = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      const ks = new Map<string, readonly unknown[]>();
      pending.forEach(t => (TABLE_KEYS[t] ?? []).forEach(k => ks.set(JSON.stringify(k), k)));
      pending.clear();
      ks.forEach(k => qc.invalidateQueries({ queryKey: k }));
    };
    let dropped = false;
    const close = openEvents({
      onTable: table => {
        pending.add(table);
        clearTimeout(timer);
        timer = setTimeout(flush, 150);
      },
      onDrop: () => {
        if (!dropped) hud('A conexão em tempo real caiu. Tentando reconectar…', 'info');
        dropped = true;
        // Pode ter sido a sessão que venceu: o `me` confere e, se for, mostra o login.
        void qc.invalidateQueries({ queryKey: ['me'] });
      },
      // Avisos podem ter se perdido enquanto a conexão estava fora: recarrega tudo.
      onReopen: () => { if (dropped) { dropped = false; void qc.invalidateQueries(); } },
    });
    return () => { clearTimeout(timer); close(); };
  }, [enabled, qc]);
}
